/*
  IT Assets Service — wraps /api/v1/org/it-assets.
*/

import apiClient from './apiService';

export type AssetType = 'HARDWARE' | 'SOFTWARE' | 'NETWORK' | 'PERIPHERAL' | 'OTHER';
export type SharingMode = 'SHARED' | 'EXCLUSIVE';

export interface ITAsset {
  id: number;
  entity_id: number;
  asset_code: string;
  code?: string; // server-generated from code_seq (e.g. IT-1); null for unsubmitted drafts
  asset_name: string;
  description?: string;
  asset_type?: AssetType;
  sharing_mode?: SharingMode;
  serial_number?: string;
  make_model?: string;
  purchase_date?: string;
  warranty_expiry?: string;
  vendor_party_id?: number | null;
  vendor_name?: string;
  location_id: number;
  location_name?: string;
  posting_id?: number | null;
  posting_label?: string | null;
  purpose?: string;
  is_active: number;
  is_approved: number;
  created_by?: number;
  created_on?: string;
  modified_by?: number;
  modified_on?: string;
  // Workflow fields (migration 105)
  workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
  submitted_by?: number;
  submitted_on?: string;
  approved_by?: number;
  approved_on?: string;
  approval_note?: string;
  rejected_by?: number;
  rejected_on?: string;
  rejection_reason?: string;
  // Joined name fields
  created_by_name?: string;
  submitted_by_name?: string;
  approved_by_name?: string;
  rejected_by_name?: string;
}

export interface ITAssetCreatePayload {
  /** Auto-generated server-side (ITA-N) — never sent from the create form. */
  asset_code?: string;
  asset_name: string;
  description?: string;
  asset_type?: AssetType;
  sharing_mode?: SharingMode;
  serial_number?: string;
  make_model?: string;
  purchase_date?: string;
  warranty_expiry?: string;
  vendor_party_id?: number | null;
  vendor_name?: string;
  location_id: number;
  posting_id?: number | null;
  purpose?: string;
}

export type ITAssetUpdatePayload = Partial<ITAssetCreatePayload> & {
  is_active?: number;
  is_approved?: number;
};

export interface ITAssetQueryParams {
  status?: 'ALL' | 'ACTIVE' | 'INACTIVE';
  search?: string;
  asset_type?: AssetType;
  sharing_mode?: SharingMode;
  location_id?: number;
  page?: number;
  limit?: number;
}

const BASE = '/api/v1/org/it-assets';

export const itAssetsService = {
  async list(params: ITAssetQueryParams = {}): Promise<ITAsset[]> {
    const qs: Record<string, string> = { limit: String(params.limit ?? 200) };
    if (params.status && params.status !== 'ALL') qs.status = params.status;
    if (params.search) qs.search = params.search;
    if (params.asset_type) qs.asset_type = params.asset_type;
    if (params.sharing_mode) qs.sharing_mode = params.sharing_mode;
    if (params.location_id) qs.location_id = String(params.location_id);
    if (params.page) qs.page = String(params.page);
    const r = await apiClient.get(BASE, { params: qs });
    return r.data?.data ?? [];
  },

  async get(id: number): Promise<ITAsset> {
    const r = await apiClient.get(`${BASE}/${id}`);
    return r.data?.data;
  },

  async create(payload: ITAssetCreatePayload): Promise<ITAsset> {
    const r = await apiClient.post(BASE, payload);
    return r.data?.data;
  },

  async update(id: number, payload: ITAssetUpdatePayload): Promise<ITAsset> {
    const r = await apiClient.put(`${BASE}/${id}`, payload);
    return r.data?.data;
  },

  async remove(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },

  async submit(id: number): Promise<void> {
    const r = await apiClient.post(`${BASE}/${id}/submit`);
    if (!r.data?.success) throw new Error(r.data?.error || 'Submit failed');
  },

  async approve(id: number, note?: string): Promise<void> {
    const r = await apiClient.post(`${BASE}/${id}/approve`, { note });
    if (!r.data?.success) throw new Error(r.data?.error || 'Approve failed');
  },

  async reject(id: number, reason: string): Promise<void> {
    const r = await apiClient.post(`${BASE}/${id}/reject`, { reason });
    if (!r.data?.success) throw new Error(r.data?.error || 'Reject failed');
  },

  async reopen(id: number): Promise<void> {
    const r = await apiClient.post(`${BASE}/${id}/reopen`);
    if (!r.data?.success) throw new Error(r.data?.error || 'Reopen failed');
  },

  async getHistory(itemId: number): Promise<any[]> {
    const response = await apiClient.get(`${BASE}/${itemId}/history`);
    if (response.data.success && response.data.data) {
      return (response.data.data as any[]).map(r => ({
        audit_id: r.id,
        action: r.event_type === 'state_transition' ? 'STATE' : 'EDIT',
        changed_by_username: r.changed_by_username || 'Unknown',
        changed_on: r.changed_on,
        change_reason: r.remarks,
      }));
    }
    throw new Error(response.data.error || 'Failed to fetch history');
  },
};

export default itAssetsService;
